import { useState } from "react";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight, Plus, X } from "lucide-react";
import { createChannel } from "../Services/MasterDataService";

function AddChannel() {
    const navigate = useNavigate();
    const { refreshChannels } = useOutletContext();
    const [channelName, setChannelName] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const handleClose = () => {
        navigate("/dashboard/MasterData");
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const trimmedName = channelName.trim();

        if (!trimmedName) {
            CustomToast("fail", "Channel name is required.");
            return;
        }

        setSubmitting(true);
        try {
            const res = await createChannel(trimmedName);
            if (res?.data?.status === "success") {
                CustomToast("success", "Channel created.");
                setChannelName("");
                await refreshChannels?.();
                handleClose();
            } else {
                CustomToast("fail", res?.data?.message || "Failed to create channel.");
            }
        } catch (error) {
            const message = error?.response?.data?.message || "Something went wrong. Please try again.";
            CustomToast("fail", message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[100] overflow-hidden">
            <motion.button
                type="button"
                aria-label="Close add channel drawer"
                onClick={handleClose}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]"
            />

            <motion.aside
                initial={{ x: "100%" }}
                animate={{ x: 0 }}
                exit={{ x: "100%" }}
                transition={{ type: "tween", duration: 0.28, ease: "easeOut" }}
                className="absolute right-0 top-0 h-full w-full max-w-2xl bg-[#f8f8fb] shadow-2xl"
            >
                <div className="flex h-full flex-col">
                    <div className="border-b border-gray-200 bg-white px-5 py-4 sm:px-6">
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900">Add Channel</h1>
                                <div className="mt-1 flex items-center gap-1.5 text-sm">
                                    <Link to="/dashboard/MasterData" className="text-[#7f35b2] hover:underline">
                                        Channels
                                    </Link>
                                    <ChevronRight size={14} className="text-gray-400" />
                                    <span className="text-gray-500">Add Channel</span>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={handleClose}
                                className="flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 text-gray-500 transition hover:border-[#7f35b2] hover:text-[#7f35b2]"
                            >
                                <X size={18} />
                            </button>
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto p-4 sm:p-5">
                        <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm sm:p-6">
                            <div className="mx-auto max-w-xl">
                                <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                                    <div>
                                        <h2 className="inline-block border-b-2 border-[#7f35b2] pb-1 text-lg font-semibold text-[#7f35b2]">
                                            Channel Details
                                        </h2>
                                        <p className="mt-2 text-sm text-gray-500">
                                            Create a new channel to make it available across assessments and responder flows.
                                        </p>
                                    </div>

                                    <div>
                                        <label className="mb-1.5 block text-sm font-medium text-gray-700">
                                            Channel Name <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            value={channelName}
                                            onChange={(e) => setChannelName(e.target.value)}
                                            placeholder="Enter channel name"
                                            className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm text-gray-700 focus:border-[#7f35b2] focus:outline-none focus:ring-1 focus:ring-[#7f35b2]"
                                        />
                                    </div>

                                    <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
                                        <p className="text-xs text-gray-400">
                                            Fields marked with <span className="text-red-500">*</span> are required
                                        </p>
                                        <div className="flex items-center gap-3">
                                            <button
                                                type="button"
                                                onClick={handleClose}
                                                disabled={submitting}
                                                className="flex items-center gap-2 rounded-lg border border-gray-200 px-5 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                                            >
                                                <X size={16} />
                                                Cancel
                                            </button>
                                            <button
                                                type="submit"
                                                disabled={submitting}
                                                className="flex items-center gap-2 rounded-lg bg-[#7f35b2] px-5 py-2.5 text-sm font-medium text-white hover:bg-[#3bb290] disabled:cursor-not-allowed disabled:opacity-60"
                                            >
                                                <Plus size={16} />
                                                {submitting ? "Adding..." : "Add Channel"}
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </motion.aside>
        </div>
    );
}

export default AddChannel;
